// Developer: Ankit Gupta
/**
 * Orion > Approvals: every step waiting for my Allow or Deny, across applications.
 *
 * In Slack a card sat in its thread and expired after fifteen minutes if the
 * approver was elsewhere. Here they are in one place, oldest first, with the
 * time left, and can be answered without opening the conversation.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Spin, Tag } from 'antd';
import { CheckOutlined, CloseOutlined, MessageOutlined } from '@ant-design/icons';
import { PageHeader } from '@/components/common/ui';
import { useT } from '@/i18n';
import { useFailureMessage } from '@/pages/Insight/errors';
import { chatApi, type WaitingApproval } from './api';
import { OrionIcon } from './OrionIcon';
import { countdown, timeAgo } from './time';
import { ReviewButtons, useWaitingReviews } from './PatchReview';
import './chat.css';

export function useWaitingApprovals() {
  return useQuery({ queryKey: ['orion-approvals'], queryFn: () => chatApi.approvals(), refetchInterval: 8000, staleTime: 4000 });
}

export default function OrionApprovals() {
  const { t } = useT('chat');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const failed = useFailureMessage();
  const waiting = useWaitingApprovals();
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  const answer = useMutation({
    mutationFn: ({ a, choice }: { a: WaitingApproval; choice: 'allow' | 'deny' }) => chatApi.approve(a.thread_id, a.approval_id, choice),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['orion-approvals'] });
      void qc.invalidateQueries({ queryKey: ['chat-threads'] });
    },
    onError: (e) => failed(t('approvals.errAnswer'), e),
  });
  const list = waiting.data ?? [];
  const reviews = useWaitingReviews().data ?? [];
  return (
    <div className="orion-apps">
      <PageHeader
        title={<span><OrionIcon size={26} /> {t('approvals.title')}</span>}
        subtitle={t('approvals.subtitle')}
        actions={<Button icon={<MessageOutlined />} onClick={() => navigate('/orion')}>{t('approvals.conversations')}</Button>}
      />
      {reviews.length > 0 && <div className="orion-section">{t('approvals.patchesToReview')}</div>}
      {reviews.map((p) => (
        <div key={`p${p.id}`} className="orion-inbox-item">
          <div className="orion-inbox-main">
            <div className="orion-inbox-lead">{p.revision > 1 ? t('approvals.patchRevision', { name: p.name, n: p.revision }) : t('approvals.patch', { name: p.name })}</div>
            <div className="orion-inbox-meta">{t('approvals.builtFor')}</div>
            <div className="orion-inbox-meta">
              <Tag>{p.application_name}</Tag>
              {t('approvals.askedIn', { who: p.requested_by, title: p.thread_title || t('approvals.aConversation') })} · {t('approvals.handedOver', { when: timeAgo(p.handed_over_at) })} · <strong>{t('approvals.noLimit')}</strong>
            </div>
          </div>
          <div className="orion-inbox-actions">
            <ReviewButtons patchId={p.id} name={p.name} />
            {p.thread_id && <Button onClick={() => navigate(`/orion/${p.thread_id}`)}>{t('approvals.open')}</Button>}
          </div>
        </div>
      ))}
      {reviews.length > 0 && list.length > 0 && <div className="orion-section">{t('approvals.stepsWaiting')}</div>}
      {waiting.isLoading ? <Spin /> : list.length === 0 ? (
        reviews.length === 0 ? <Alert type="success" showIcon message={t('approvals.nothingTitle')} description={t('approvals.nothingBody')} /> : null
      ) : list.map((a) => (
        <div key={a.approval_id} className="orion-inbox-item">
          <div className="orion-inbox-main">
            <div className="orion-inbox-lead" dir="auto">{a.lead || t('approvals.aStep', { tool: a.tool_name })}</div>
            {a.headline && <div className="orion-inbox-meta" dir="auto">{a.headline}</div>}
            <div className="orion-inbox-meta">
              <Tag>{a.application}</Tag>
              {t('approvals.askedIn', { who: a.requested_by, title: a.thread_title || t('approvals.aConversation') })} · {timeAgo(a.created_at)} · <strong>{countdown(a.expires_at)}</strong>
              {a.required > 1 && <> · {t('approvals.approvalsOf', { n: a.approvers.length, required: a.required })}{a.approvers.length ? ` (${a.approvers.join(', ')})` : ''}</>}
            </div>
          </div>
          <div className="orion-inbox-actions">
            <Button type="primary" icon={<CheckOutlined />} loading={answer.isPending} onClick={() => answer.mutate({ a, choice: 'allow' })}>{t('approvals.allow')}</Button>
            <Button danger icon={<CloseOutlined />} disabled={answer.isPending} onClick={() => answer.mutate({ a, choice: 'deny' })}>{t('approvals.deny')}</Button>
            <Button onClick={() => navigate(`/orion/${a.thread_id}`)}>{t('approvals.open')}</Button>
          </div>
        </div>
      ))}
    </div>
  );
}
